import { AuditLogListItem } from '@models/resources/audit-log.model';

/**
 * Demo-Audit-Logs für die Admin-Transaktionsprüfung (Sprint 3 Tag 5).
 *
 * Pro Transaction-Request gibt es:
 * - einen `created`-Eintrag beim Einreichen (vom Customer)
 * - einen `status_changed`-Eintrag pro Status-Übergang (vom Staff)
 *
 * Die Einträge sind bewusst nur für eine Auswahl von TX angelegt —
 * das reicht, um in Demo/Review zu zeigen, wie die `AuditHistory`
 * im Detail-Screen aussieht. Backend liefert ab Sprint 5 den echten
 * Stream.
 */
const STAFF_USER = {
  id: 42,
  email: 'staff@aurumconsulting.at',
  role: 'staff' as const,
};

const CUSTOMER_1 = {
  id: 1,
  email: 'customer@goldapp.local',
  role: 'customer' as const,
};

const CUSTOMER_2 = {
  id: 2,
  email: 'elena.weinberger@example.at',
  role: 'customer' as const,
};

const CUSTOMER_4 = {
  id: 4,
  email: 'sabine.klug@example.com',
  role: 'customer' as const,
};

export const DEMO_AUDIT_LOGS: AuditLogListItem[] = [
  // --- TX #101 (Holzner, open, Gold-Kauf 100 g) ------------------------
  {
    id: 1001,
    user_id: CUSTOMER_1.id,
    auditable_type: 'TransactionRequest',
    auditable_id: 101,
    action: 'created',
    old_values: null,
    new_values: { status: 'open' },
    comment: 'Monatliche Aufstockung',
    ip_address: '10.8.0.12',
    created_at: '2026-04-15T09:02:00+02:00',
    user: CUSTOMER_1,
  },

  // --- TX #100 (Holzner, in_review, Silber-Verkauf 500 g) --------------
  {
    id: 1002,
    user_id: CUSTOMER_1.id,
    auditable_type: 'TransactionRequest',
    auditable_id: 100,
    action: 'created',
    old_values: null,
    new_values: { status: 'open' },
    comment: null,
    ip_address: '10.8.0.12',
    created_at: '2026-04-12T16:44:00+02:00',
    user: CUSTOMER_1,
  },
  {
    id: 1003,
    user_id: STAFF_USER.id,
    auditable_type: 'TransactionRequest',
    auditable_id: 100,
    action: 'status_changed',
    old_values: { status: 'open' },
    new_values: { status: 'in_review' },
    comment: 'Beleg angefordert.',
    ip_address: '10.8.1.2',
    created_at: '2026-04-13T08:11:00+02:00',
    user: STAFF_USER,
  },

  // --- TX #97 (Holzner, approved, Gold-Kauf 250 g) ---------------------
  {
    id: 1004,
    user_id: CUSTOMER_1.id,
    auditable_type: 'TransactionRequest',
    auditable_id: 97,
    action: 'created',
    old_values: null,
    new_values: { status: 'open' },
    comment: null,
    ip_address: '10.8.0.12',
    created_at: '2026-03-28T11:20:00+01:00',
    user: CUSTOMER_1,
  },
  {
    id: 1005,
    user_id: STAFF_USER.id,
    auditable_type: 'TransactionRequest',
    auditable_id: 97,
    action: 'status_changed',
    old_values: { status: 'open' },
    new_values: { status: 'in_review' },
    comment: null,
    ip_address: '10.8.1.2',
    created_at: '2026-03-28T14:05:00+01:00',
    user: STAFF_USER,
  },
  {
    id: 1006,
    user_id: STAFF_USER.id,
    auditable_type: 'TransactionRequest',
    auditable_id: 97,
    action: 'status_changed',
    old_values: { status: 'in_review' },
    new_values: { status: 'approved' },
    comment: 'Dokumentation vollständig, Freigabe erteilt.',
    ip_address: '10.8.1.2',
    created_at: '2026-03-29T10:05:00+02:00',
    user: STAFF_USER,
  },

  // --- TX #91 (Holzner, rejected) --------------------------------------
  {
    id: 1007,
    user_id: CUSTOMER_1.id,
    auditable_type: 'TransactionRequest',
    auditable_id: 91,
    action: 'created',
    old_values: null,
    new_values: { status: 'open' },
    comment: null,
    ip_address: '10.8.0.12',
    created_at: '2026-02-28T15:10:00+01:00',
    user: CUSTOMER_1,
  },
  {
    id: 1008,
    user_id: STAFF_USER.id,
    auditable_type: 'TransactionRequest',
    auditable_id: 91,
    action: 'status_changed',
    old_values: { status: 'open' },
    new_values: { status: 'in_review' },
    comment: null,
    ip_address: '10.8.1.2',
    created_at: '2026-02-28T17:40:00+01:00',
    user: STAFF_USER,
  },
  {
    id: 1009,
    user_id: STAFF_USER.id,
    auditable_type: 'TransactionRequest',
    auditable_id: 91,
    action: 'status_changed',
    old_values: { status: 'in_review' },
    new_values: { status: 'rejected' },
    comment: 'Abgelehnt: Mindesthaltefrist noch nicht erreicht.',
    ip_address: '10.8.1.2',
    created_at: '2026-03-01T09:30:00+01:00',
    user: STAFF_USER,
  },

  // --- TX #81 (Weinberger, in_review, Gold-Kauf 400 g) -----------------
  {
    id: 1010,
    user_id: CUSTOMER_2.id,
    auditable_type: 'TransactionRequest',
    auditable_id: 81,
    action: 'created',
    old_values: null,
    new_values: { status: 'open' },
    comment: 'Institutionelle Aufstockung',
    ip_address: '10.8.0.22',
    created_at: '2026-04-14T10:30:00+02:00',
    user: CUSTOMER_2,
  },
  {
    id: 1011,
    user_id: STAFF_USER.id,
    auditable_type: 'TransactionRequest',
    auditable_id: 81,
    action: 'status_changed',
    old_values: { status: 'open' },
    new_values: { status: 'in_review' },
    comment: 'AML-Rückfrage offen.',
    ip_address: '10.8.1.2',
    created_at: '2026-04-15T08:22:00+02:00',
    user: STAFF_USER,
  },

  // --- TX #79 (Klug, approved, Gold-Kauf 800 g) -----------------------
  {
    id: 1012,
    user_id: CUSTOMER_4.id,
    auditable_type: 'TransactionRequest',
    auditable_id: 79,
    action: 'created',
    old_values: null,
    new_values: { status: 'open' },
    comment: null,
    ip_address: '10.8.0.48',
    created_at: '2026-03-20T09:45:00+01:00',
    user: CUSTOMER_4,
  },
  {
    id: 1013,
    user_id: STAFF_USER.id,
    auditable_type: 'TransactionRequest',
    auditable_id: 79,
    action: 'status_changed',
    old_values: { status: 'open' },
    new_values: { status: 'in_review' },
    comment: null,
    ip_address: '10.8.1.2',
    created_at: '2026-03-20T16:30:00+01:00',
    user: STAFF_USER,
  },
  {
    id: 1014,
    user_id: STAFF_USER.id,
    auditable_type: 'TransactionRequest',
    auditable_id: 79,
    action: 'status_changed',
    old_values: { status: 'in_review' },
    new_values: { status: 'approved' },
    comment: null,
    ip_address: '10.8.1.2',
    created_at: '2026-03-21T14:12:00+01:00',
    user: STAFF_USER,
  },

  // --- Lagergebühr-Rechnungen (S3-9, Konzept §7.4 + ADR-003 D4) -------
  // Audit-Einträge für `paid_at_set`/`paid_at_cleared` werden zur
  // Laufzeit auch im Dummy-Modus angelegt (siehe `DummyAdminStorageFeesFacade`).
  // Hier seed-Einträge für die initial bereits als `paid` ausgewiesenen
  // Rechnungen, damit die Customer-Detail-Sektion „Lagergebühren" eine
  // realistische Historie zeigt.
  {
    id: 2001,
    user_id: STAFF_USER.id,
    auditable_type: 'StorageFeeInvoice',
    auditable_id: 6001,
    action: 'paid_at_set',
    old_values: { paid_at: null },
    new_values: { paid_at: '2025-04-02T11:42:00+02:00' },
    comment: null,
    ip_address: '10.8.1.2',
    created_at: '2025-04-02T11:42:00+02:00',
    user: STAFF_USER,
  },
  {
    id: 2002,
    user_id: STAFF_USER.id,
    auditable_type: 'StorageFeeInvoice',
    auditable_id: 6003,
    action: 'paid_at_set',
    old_values: { paid_at: null },
    new_values: { paid_at: '2024-07-15T09:30:00+02:00' },
    comment: null,
    ip_address: '10.8.1.2',
    created_at: '2024-07-15T09:30:00+02:00',
    user: STAFF_USER,
  },
  {
    id: 2003,
    user_id: STAFF_USER.id,
    auditable_type: 'StorageFeeInvoice',
    auditable_id: 6004,
    action: 'paid_at_set',
    old_values: { paid_at: null },
    new_values: { paid_at: '2025-07-09T14:12:00+02:00' },
    comment: null,
    ip_address: '10.8.1.2',
    created_at: '2025-07-09T14:12:00+02:00',
    user: STAFF_USER,
  },
  {
    id: 2004,
    user_id: STAFF_USER.id,
    auditable_type: 'StorageFeeInvoice',
    auditable_id: 6006,
    action: 'paid_at_set',
    old_values: { paid_at: null },
    new_values: { paid_at: '2023-05-29T10:11:00+02:00' },
    comment: null,
    ip_address: '10.8.1.2',
    created_at: '2023-05-29T10:11:00+02:00',
    user: STAFF_USER,
  },
  {
    id: 2005,
    user_id: STAFF_USER.id,
    auditable_type: 'StorageFeeInvoice',
    auditable_id: 6007,
    action: 'paid_at_set',
    old_values: { paid_at: null },
    new_values: { paid_at: '2023-11-22T15:45:00+01:00' },
    comment: null,
    ip_address: '10.8.1.2',
    created_at: '2023-11-22T15:45:00+01:00',
    user: STAFF_USER,
  },
  {
    id: 2006,
    user_id: STAFF_USER.id,
    auditable_type: 'StorageFeeInvoice',
    auditable_id: 6008,
    action: 'paid_at_set',
    old_values: { paid_at: null },
    new_values: { paid_at: '2024-06-04T08:50:00+02:00' },
    comment: null,
    ip_address: '10.8.1.2',
    created_at: '2024-06-04T08:50:00+02:00',
    user: STAFF_USER,
  },
  {
    id: 2007,
    user_id: STAFF_USER.id,
    auditable_type: 'StorageFeeInvoice',
    auditable_id: 6009,
    action: 'paid_at_set',
    old_values: { paid_at: null },
    new_values: { paid_at: '2024-11-30T12:18:00+01:00' },
    comment: null,
    ip_address: '10.8.1.2',
    created_at: '2024-11-30T12:18:00+01:00',
    user: STAFF_USER,
  },
  {
    id: 2008,
    user_id: STAFF_USER.id,
    auditable_type: 'StorageFeeInvoice',
    auditable_id: 6012,
    action: 'paid_at_set',
    old_values: { paid_at: null },
    new_values: { paid_at: '2023-03-08T09:15:00+01:00' },
    comment: null,
    ip_address: '10.8.1.2',
    created_at: '2023-03-08T09:15:00+01:00',
    user: STAFF_USER,
  },
  {
    id: 2009,
    user_id: STAFF_USER.id,
    auditable_type: 'StorageFeeInvoice',
    auditable_id: 6013,
    action: 'paid_at_set',
    old_values: { paid_at: null },
    new_values: { paid_at: '2024-03-04T11:45:00+01:00' },
    comment: null,
    ip_address: '10.8.1.2',
    created_at: '2024-03-04T11:45:00+01:00',
    user: STAFF_USER,
  },
];
